import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["children", "overlay", "placement", "withPortal", "closeOverlay"];
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
import React from 'react';
import { ENCORE_DATA_IDS } from "../../dataEncoreIds.mjs";
import { deprecatedOverlayDirections, mapPhysicalToLogicalDirections } from "../../styles/mixins/index.mjs";
import { OverlayPortal } from "../OverlayPortal/index.mjs";
import { Overlay } from "./Overlay.mjs";
import { Trigger } from "./Trigger.mjs";
import { physicalDirectionWarning } from "../../logging.mjs";
var OverlayTriggerComponent = /*#__PURE__*/React.forwardRef(function OverlayTriggerComponent(_ref, ref) {
  var children = _ref.children,
    overlay = _ref.overlay,
    placement = _ref.placement,
    withPortal = _ref.withPortal,
    closeOverlay = _ref.closeOverlay,
    props = _objectWithoutProperties(_ref, _excluded);
  if (placement) {
    physicalDirectionWarning(placement, 'OverlayTrigger');
  }
  return /*#__PURE__*/React.createElement(Trigger, Object.assign({}, props, {
    ref: ref,
    "data-encore-id": ENCORE_DATA_IDS.OverlayTrigger
  }), children, overlay && (withPortal && typeof ref !== 'function' && ref !== null && ref !== void 0 && ref.current ? /*#__PURE__*/React.createElement(OverlayPortal, {
    overlayPosition: ref.current.getBoundingClientRect(),
    closeOverlay: closeOverlay
  }, /*#__PURE__*/React.createElement(Overlay, {
    placement: mapPhysicalToLogicalDirections(placement)
  }, overlay)) : /*#__PURE__*/React.createElement(Overlay, {
    placement: mapPhysicalToLogicalDirections(placement)
  }, overlay)));
});
var overlayTriggerHelpers = _objectSpread({}, deprecatedOverlayDirections);

/**
* **OverlayTrigger** ![Status: Production](https://img.shields.io/badge/PRODUCTION-%2357B560|height=14)
*
* [GitHub](https://ghe.spotify.net/encore/web/tree/master/src/core/components/OverlayTrigger) 
*
* Our Triggers are not viewport-aware and may render off screen in more dynamic situations. Consider a library such as [Tippy.mjs](https://atomiks.github.io/tipp.mjs/) or [Floating UI](https://floating-ui.com/) if you need smarter positioning.
*
* [Read the full component documentation →](https://encore.spotify.net/web/components/non-visual?format=figma-figma#overlaytrigger)
*
* @example
* () => {
*  const [isActiveTop, toggleActiveTop] = useState(false);
*  const [isActiveTopRight, toggleActiveTopRight] = useState(false);
*  const [isActiveTopLeft, toggleActiveTopLeft] = useState(false);
*  const [isActiveLeft, toggleActiveLeft] = useState(false);
*  const [isActiveLeftTop, toggleActiveLeftTop] = useState(false);
*  const [isActiveLeftBottom, toggleActiveLeftBottom] = useState(false);
*  const [isActiveBottom, toggleActiveBottom] = useState(false);
*  const [isActiveBottomLeft, toggleActiveBottomLeft] = useState(false);
*  const [isActiveBottomRight, toggleActiveBottomRight] = useState(false);
*  const [isActiveRight, toggleActiveRight] = useState(false);
*  const [isActiveRightTop, toggleActiveRightTop] = useState(false);
*  const [isActiveRightBottom, toggleActiveRightBottom] = useState(false);
*
*  return (
*    <Table>
*      <caption>Different overlay placements</caption>
*      <tbody>
*        <TableRow>
*          <TableCell>
*            <OverlayTrigger
*              placement="topStart"
*              overlay={
*                isActiveTopLeft && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveTopLeft}
*                onClick={() => toggleActiveTopLeft(!isActiveTopLeft)}
*                size="small"
*              >
*                Toggle top left
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*          <TableCell align="center">
*            <OverlayTrigger
*              placement="top"
*              overlay={
*                isActiveTop && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveTop}
*                onClick={() => toggleActiveTop(!isActiveTop)}
*                size="small"
*              >
*                Toggle top
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*          <TableCell align="right">
*            <OverlayTrigger
*              placement="topEnd"
*              overlay={
*                isActiveTopRight && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveTopRight}
*                onClick={() => toggleActiveTopRight(!isActiveTopRight)}
*                size="small"
*              >
*                Toggle top right
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*        </TableRow>
*        <TableRow>
*          <TableCell>
*            <OverlayTrigger
*              placement="startTop"
*              overlay={
*                isActiveLeftTop && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveLeftTop}
*                onClick={() => toggleActiveLeftTop(!isActiveLeftTop)}
*                size="small"
*              >
*                Toggle left top
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*          <TableCell>&nbsp;</TableCell>
*          <TableCell align="right">
*            <OverlayTrigger
*              placement="endTop"
*              overlay={
*                isActiveRightTop && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveRightTop}
*                onClick={() => toggleActiveRightTop(!isActiveRightTop)}
*                size="small"
*              >
*                Toggle right top
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*        </TableRow>
*        <TableRow>
*          <TableCell>
*            <OverlayTrigger
*              placement="start"
*              overlay={
*                isActiveLeft && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveLeft}
*                onClick={() => toggleActiveLeft(!isActiveLeft)}
*                size="small"
*              >
*                Toggle left
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*          <TableCell>&nbsp;</TableCell>
*          <TableCell align="right">
*            <OverlayTrigger
*              placement="end"
*              overlay={
*                isActiveRight && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveRight}
*                onClick={() => toggleActiveRight(!isActiveRight)}
*                size="small"
*              >
*                Toggle right
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*        </TableRow>
*        <TableRow>
*          <TableCell>
*            <OverlayTrigger
*              placement="startBottom"
*              overlay={
*                isActiveLeftBottom && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveLeftBottom}
*                onClick={() => toggleActiveLeftBottom(!isActiveLeftBottom)}
*                size="small"
*              >
*                Toggle left bottom
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*          <TableCell>&nbsp;</TableCell>
*          <TableCell align="right">
*            <OverlayTrigger
*              placement="endBottom"
*              overlay={
*                isActiveRightBottom && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveRightBottom}
*                onClick={() => toggleActiveRightBottom(!isActiveRightBottom)}
*                size="small"
*              >
*                Toggle right bottom
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*        </TableRow>
*        <TableRow>
*          <TableCell>
*            <OverlayTrigger
*              placement="bottomStart"
*              overlay={
*                isActiveBottomLeft && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveBottomLeft}
*                onClick={() => toggleActiveBottomLeft(!isActiveBottomLeft)}
*                size="small"
*              >
*                Toggle bottom left
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*          <TableCell align="center">
*            <OverlayTrigger
*              placement="bottom"
*              overlay={
*                isActiveBottom && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveBottom}
*                onClick={() => toggleActiveBottom(!isActiveBottom)}
*                size="small"
*              >
*                Toggle bottom
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*          <TableCell align="right">
*            <OverlayTrigger
*              placement="bottomEnd"
*              overlay={
*                isActiveBottomRight && (
*                  <Tooltip>Aenean lacinia bibendum nulla sed consectetur. Cras justo.</Tooltip>
*                )
*              }
*            >
*              <ButtonTertiary
*                condensed
*                aria-expanded={isActiveBottomRight}
*                onClick={() => toggleActiveBottomRight(!isActiveBottomRight)}
*                size="small"
*              >
*                Toggle bottom right
*              </ButtonTertiary>
*            </OverlayTrigger>
*          </TableCell>
*        </TableRow>
*      </tbody>
*    </Table>
*  );
*};
*
*/
export var OverlayTrigger = Object.assign(OverlayTriggerComponent, overlayTriggerHelpers);